/* BeautyVes PDP: mockup motion, Shopify commerce, scoped lifecycle. No simulated cart. */
(() => {
  'use strict';
  if (window.BvPdpLifecycle) return;
  const instances = new Map();
  const clamp = n => Math.min(1, Math.max(0, n));
  const seg = (p, a, b) => clamp((p - a) / (b - a));
  const ease = x => x < .5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
  const visible = el => el && !el.closest('[hidden],[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden';
  const buzz = ms => { try { navigator.vibrate?.(ms); } catch (_) { /* optional hardware */ } };
  const pictureURL = (src, width) => {
    const u = new URL(src, location.href);
    if (!['https:', 'http:'].includes(u.protocol)) return '';
    if (width && (/\/s\/files\/|\/cdn\/shop\/files\//.test(u.pathname))) u.searchParams.set('width', width);
    return u.href;
  };
  class BvPdp {
    constructor(root) {
      this.root = root;
      this.config = JSON.parse(root.querySelector('[data-bv-config]').textContent);
      this.abort = new AbortController();
      this.timers = new Set();
      this.frames = new Set();
      this.panels = [];
      this.destroyed = false;
      this.busy = false;
      this.cart = null;
      this.fresh = new Set();
      this.current = Math.max(0, this.config.variants.findIndex(v => String(v.id) === String(this.config.selectedVariantId)));
      this.pairIndex = this.defaultPair(this.current);
      this.pairOn = false;
      this.rec = -1;
      this.q1 = -1;
      this.q2 = -1;
      this.galleryIndex = 0;
      this.variantRevision = 0;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.overlayKey = `bv-${root.dataset.sectionId || this.config.product.id}`;
      this.overlay = null;
      this.historyPending = false;
      this.cartOpenRevision = 0;
      this.cartOpenPending = false;
      this.pendingOverlayOpen = null;
      this.$ = s => root.querySelector(s) || this.panels.map(p => p.matches(s) ? p : p.querySelector(s)).find(Boolean);
      this.$$ = s => [...root.querySelectorAll(s), ...this.panels.flatMap(p => [...p.querySelectorAll(s)])];
      this.t = (key, values = {}) => {
        const text = String(this.config.labels?.[key] ?? '');
        return text.replace(/\{(\w+)\}/g, (m, k) => values[k] == null ? m : String(values[k]));
      };
      this.money = cents => {
        const parts = new Intl.NumberFormat('it-IT', { style: 'currency', currency: this.cart?.currency || this.config.product.currency || 'EUR' }).formatToParts(Number(cents) / 100);
        return `${parts.find(p => p.type === 'currency')?.value || ''} ${parts.filter(p => p.type !== 'currency' && p.type !== 'literal').map(p => p.value).join('')}`;
      };
      this.route = path => `${window.Shopify?.routes?.root || '/'}${path}`;
      this.root.dataset.bvReady = 'true';
      root.querySelectorAll('[data-bv-module="foglio"],[data-bv-module="foto"],[data-bv-module="avviso"]').forEach(panel => {
        panel.classList.add('bv-panel');
        panel.dataset.bvOwner = this.overlayKey;
        document.body.append(panel);
        this.panels.push(panel);
      });
      this.sheet = this.$('#sheet');
      this.lb = this.$('#lb');
      this.gallery = this.$('#gal');
      this.toastEl = this.$('#toast');
      try {
        this.bind();
        this.restoreFavorite();
        this.render(true);
        this.initMotion();
        this.measureHeader();
        this.initWhatsAppClearance();
        this.initPaymentWidgetPlacement();
      } catch (error) { this.destroy(); throw error; }
    }
    on(el, event, fn, options = {}) {
      el?.addEventListener(event, fn, { ...options, signal: this.abort.signal });
    }
    initPaymentWidgetPlacement() {
      const form = this.root.querySelector('.bv-pdp__form');
      const slot = this.root.querySelector('[data-bv-payment-slot]');
      if (!form || !slot) return;
      let widget = null;
      let origin = null;
      const move = () => {
        if (widget) return;
        const found = form.querySelector('#ultimateTrustBadgeswidgetDiv');
        if (!found) return;
        origin = document.createComment('bv-pdp-payment-origin');
        form.insertBefore(origin, found);
        widget = found;
        slot.append(widget);
        slot.hidden = false;
      };
      const observer = new MutationObserver(move);
      observer.observe(form, { childList: true });
      move();
      this.paymentPlacement = { cleanup: () => {
        observer.disconnect();
        if (widget && origin?.parentNode) origin.parentNode.insertBefore(widget, origin);
        origin?.remove();
        slot.hidden = true;
      } };
    }
    later(fn, ms) {
      const id = setTimeout(() => { this.timers.delete(id); if (!this.destroyed) fn(); }, ms);
      this.timers.add(id); return id;
    }
    raf(fn) {
      const id = requestAnimationFrame(() => { this.frames.delete(id); if (!this.destroyed) fn(); });
      this.frames.add(id); return id;
    }
    get selected() { return this.config.variants[this.current]; }
    get pair() { return this.config.pair?.variants?.[this.pairIndex]; }
    defaultPair(index) {
      const all = this.config.pair?.variants || [];
      const mapped = this.config.pair?.map?.[index];
      return mapped != null && all[mapped]?.available ? mapped : Math.max(0, all.findIndex(v => v.available));
    }
    text(selector, value) { this.$$(selector).forEach(el => { el.textContent = value; }); }
    setImage(el, src, alt, gallery = false) {
      if (!el || !src) return;
      el.src = pictureURL(src, gallery ? 800 : 0);
      if (gallery && /\/s\/files\/|\/cdn\/shop\/files\//.test(src)) {
        el.srcset = [500, 800, 1100].map(w => `${pictureURL(src, w)} ${w}w`).join(', ');
        el.sizes = '(max-width: 440px) 82vw, 360px';
      } else el.removeAttribute('srcset');
      if (alt != null) el.alt = alt;
    }
    render(rebuildGallery = false) {
      const s = this.selected, p = this.pair;
      if (!p?.available) this.pairOn = false;
      const price = s.priceCents + (this.pairOn ? p.priceCents : 0);
      [this.root, ...this.panels].forEach(el => {
        for (const [key, val] of Object.entries({ s: s.accent, sl: s.light, sd: s.deep })) if (val) el.style.setProperty(`--bv-${key}`, val);
      });
      this.text('.js-sname', s.label || s.title);
      this.text('.js-mood', s.mood || '');
      this.text('.js-fam', s.family || '');
      this.text('.js-pen', p?.label || p?.title || '');
      this.text('[data-bv="prezzo"],.js-price', this.money(s.priceCents));
      this.text('.js-pair-price', p ? this.money(p.priceCents) : '');
      this.text('[data-availability-text]', !s.available ? this.t('soldOut') : this.config.variants.length > 1 && this.config.variants.every(v => v.available) ? this.t('allAvailable', { count: this.config.variants.length }) : this.t('available'));
      this.$$('[data-availability-text]').forEach(el => { el.dataset.available = String(s.available); });
      this.$$('#swatches .sw').forEach((b, i) => {
        b.setAttribute('aria-checked', String(i === this.current));
        b.tabIndex = i === this.current ? 0 : -1;
        b.dataset.mine = String(i === this.rec);
      });
      this.$$('#wornPills .pill,#rail .rd').forEach(b => b.setAttribute('aria-pressed', String(Number(b.dataset.i) === this.current)));
      this.$$('#penPills .pill,#pairPills .pill,#sheetPairPills .pill').forEach(b => { const i = Number(b.dataset.p); b.setAttribute('aria-pressed', String(i === this.pairIndex)); b.disabled = this.busy || !this.config.pair.variants[i]?.available; });
      this.$$('#swatches .sw,#wornPills .pill,#rail .rd,#stRow .st-sw').forEach(b => { b.disabled = this.busy; });
      this.$$('.js-swimg').forEach(el => this.setImage(el, s.swatch || s.hero || s.image));
      this.$$('.js-swhd').forEach(el => this.setImage(el, s.texture || s.swatch || s.hero));
      this.$$('.js-bottle').forEach(el => this.setImage(el, s.hero || s.image));
      this.setImage(this.$('#wornImg'), s.worn, s.wornAlt);
      this.text('[data-bv-worn-caption]', s.wornCaption || '');
      const pairImage = this.config.product.single ? p?.swatch || p?.image : p?.image;
      this.setImage(this.$('#penImg'), pairImage);
      this.setImage(this.$('#shPenImg'), pairImage);
      this.setImage(this.$('#pairPhoto'), pairImage);
      const toggle = this.$('#penToggle');
      toggle?.setAttribute('aria-pressed', String(this.pairOn));
      if (toggle) toggle.disabled = !p?.available;
      const pills = this.$('#penPills');
      if (pills) pills.hidden = !this.config.product.single && (!this.pairOn || this.config.pair.variants.length < 2);
      const pairName = this.config.product.handle === '04-correttore-massima-coprenza-copia' ? String(this.config.pair?.title || '').replace(/\s*[–-]\s*BeautyVes\s*$/, '') : p?.label || p?.title;
      const useFamily = ['contorno-occhi-acido-ialuronico-anti-rughe', 'contorno-occhi-caffeina-anti-borse', 'pochette-beautyves'].includes(this.config.product.handle);
      const primaryName = useFamily ? s.family || s.label || s.title : this.pairOn ? s.name || s.label || s.title : s.label || s.title;
      const stickyName = this.pairOn ? `${primaryName} + ${pairName}` : primaryName;
      this.$$('.js-sub').forEach(el => {
        const name = el.querySelector('.bv-pdp__sticky-name');
        const money = el.querySelector('.bv-pdp__sticky-price');
        if (name && money) {
          name.textContent = stickyName;
          money.textContent = this.money(price);
        } else el.textContent = `${stickyName} · ${this.money(price)}`;
      });
      this.$$('.js-penadd').forEach(b => {
        b.textContent = this.config.product.single ? this.t('pair') : this.pairOn ? this.t('pairAdded') : this.t('pair');
        if (this.config.product.single) b.removeAttribute('aria-pressed'); else b.setAttribute('aria-pressed', String(this.pairOn));
        if (['primer-matte-affina-pori', 'primer-effetto-glow-con-spf30'].includes(this.config.product.handle)) b.setAttribute('aria-label', `${this.t('pair')} ${p?.label || p?.title || ''} · ${p ? this.money(p.priceCents) : ''}`);
        b.disabled = this.busy || !p?.available;
      });
      if (this.config.product.single) this.$('#shPenAdd')?.setAttribute('aria-label', `${this.t('pairUpsell')} ${p?.label || p?.title || ''} · ${p ? this.money(p.priceCents) : ''}`);
      this.$$('input[name="id"],select[name="id"]').forEach(el => { el.value = s.id; });
      this.$$('.js-add,#qAdd,#shPenAdd,#shMini button').forEach(b => {
        const unavailable = b.id === 'qAdd' ? !this.config.variants[this.rec]?.available : b.id === 'shPenAdd' ? !p?.available : b.closest('#shMini') ? !this.config.variants[Number(b.dataset.i)]?.available : !s.available;
        b.disabled = this.busy || unavailable;
        b.setAttribute('aria-busy', String(this.busy));
      });
      this.text('.js-cta', this.busy ? this.t('adding') : !s.available ? this.t('soldOut') : this.justAdded ? this.t('added') : this.t('add'));
      this.$('#buyNow')?.setAttribute('aria-disabled', String(this.busy || !s.available));
      this.$('#shCheckout')?.setAttribute('aria-disabled', String(this.busy));
      if (rebuildGallery) this.renderGallery();
    }
    renderGallery() {
      const data = this.selected.gallery || [];
      if (!data.length || !this.gallery) return;
      const oldSlides = [...this.gallery.children];
      data.forEach((item, i) => {
        let slide = oldSlides[i];
        if (!slide) { slide = document.createElement('div'); slide.className = 'slide'; slide.dataset.bv = 'slide'; slide.append(document.createElement('img')); this.gallery.append(slide); }
        slide.hidden = false;
        slide.dataset.bv = 'slide';
        let img = slide.querySelector('img');
        if (!img) { img = document.createElement('img'); slide.prepend(img); }
        img.width = item.width || 800; img.height = item.height || 1000;
        img.loading = i ? 'lazy' : 'eager';
        img.fetchPriority = i ? 'auto' : 'high';
        img.decoding = 'async'; img.style.objectFit = item.contain ? 'contain' : 'cover';
        this.setImage(img, item.src, item.alt, true);
        let chip = slide.querySelector('.chip');
        if (item.caption) {
          if (!chip) { chip = document.createElement('span'); chip.className = 'lgs chip'; slide.append(chip); }
          chip.textContent = item.caption; chip.hidden = false;
        } else if (chip) chip.hidden = true;
      });
      oldSlides.slice(data.length).forEach(el => el.remove());
      const dots = this.$('#dots');
      if (dots && dots.children.length !== data.length) {
        dots.replaceChildren(...data.map(() => document.createElement('span')));
      }
      const zoom = this.$('#gZoom');
      // Keep the exact SVG and counter node; update the total without replacing the control.
      const total = zoom?.querySelector('[data-gallery-total]');
      if (total) total.textContent = data.length;
      this.updateDots(0);
    }
    updateDots(i) {
      this.galleryIndex = Math.max(0, Math.min((this.selected.gallery?.length || 1) - 1, i));
      this.$$('#dots span').forEach((el, k) => el.classList.toggle('on', k === this.galleryIndex));
      this.text('#gNum', this.galleryIndex + 1);
    }
    setShade(i) {
      if (this.busy || !Number.isInteger(i) || !this.config.variants[i] || i === this.current) return;
      if (!this.overlay && (this.cartOpenPending || this.pendingOverlayOpen)) this.cancelPendingOverlayOpen();
      this.justAdded = false; clearTimeout(this.addedTimer);
      this.current = i; this.pairIndex = this.defaultPair(i); this.variantRevision++;
      this.render(true); this.gallery?.scrollTo({ left: 0, behavior: 'instant' }); buzz(8);
      const revision = this.variantRevision;
      const image = this.gallery?.querySelector('img');
      const swap = () => {
        if (this.destroyed || revision !== this.variantRevision) return;
        this.gallery.classList.remove('swap'); void this.gallery.offsetWidth; this.gallery.classList.add('swap');
      };
      if (image?.complete && image.naturalWidth) swap(); else this.on(image, 'load', swap, { once: true });
      const url = new URL(location.href); url.searchParams.set('variant', this.selected.id);
      try { history.replaceState(history.state, '', url); } catch (_) { /* privacy mode */ }
    }
    toast(message, duration = 2200) {
      if (!this.toastEl || !message) return;
      this.toastEl.textContent = message; this.toastEl.hidden = false;
      clearTimeout(this.toastTimer);
      this.toastTimer = this.later(() => { this.toastEl.hidden = true; }, duration);
    }
    togglePair() {
      if (!this.pair?.available || this.busy) return;
      this.justAdded = false; clearTimeout(this.addedTimer);
      this.pairOn = !this.pairOn; this.render();
      this.toast(this.pairOn ? this.t('addedPair', { price: this.money(this.selected.priceCents + this.pair.priceCents) }) : this.t('removedPair'));
    }
    restoreFavorite() {
      try { const handles = JSON.parse(localStorage.getItem('bv-pdp-favorites') || '[]'); this.$('#fav')?.setAttribute('aria-pressed', String(Array.isArray(handles) && handles.includes(this.config.product.handle))); } catch (_) { /* blocked storage */ }
    }
    favorite() {
      const button = this.$('#fav'), on = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(on));
      try {
        const stored = JSON.parse(localStorage.getItem('bv-pdp-favorites') || '[]');
        const handles = new Set(Array.isArray(stored) ? stored : []);
        if (on) handles.add(this.config.product.handle); else handles.delete(this.config.product.handle);
        localStorage.setItem('bv-pdp-favorites', JSON.stringify([...handles]));
      } catch (_) { /* current-page state remains usable */ }
      this.toast(this.t(on ? 'favoriteAdded' : 'favoriteRemoved'));
    }
    async share() {
      const url = new URL(this.config.product.canonicalUrl || this.config.product.url, location.origin); url.searchParams.set('variant', this.selected.id);
      try {
        if (navigator.share) await navigator.share({ title: `${this.config.product.title} – ${this.selected.label || this.selected.title}`, text: this.t('shareText'), url: url.href });
        else { await navigator.clipboard.writeText(url.href); this.toast(this.t('linkCopied')); }
      } catch (error) { if (error.name !== 'AbortError') this.toast(this.t('copyFailed')); }
    }
    async request(path, options = {}) {
      const response = await fetch(this.route(path), { credentials: 'same-origin', cache: 'no-store', signal: this.abort.signal, ...options });
      let data; try { data = await response.json(); } catch (_) { throw new Error(this.t('cartError')); }
      if (!response.ok || data.status >= 400) {
        const error = new Error(typeof data.description === 'string' ? data.description : this.t('cartError'));
        error.status = response.status; throw error;
      }
      return data;
    }
    async add(items, checkout = false) {
      if (this.busy || !items.length) return;
      const fromSheet = this.overlay === this.sheet;
      this.busy = true; this.justAdded = false; this.render();
      let posted = false;
      try {
        const result = await this.request('cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections: ['cart-drawer', 'cart-icon-bubble'], sections_url: location.pathname })
        });
        posted = true;
        this.cart = await this.request('cart.js');
        this.fresh = new Set(items.map(item => String(item.id)));
        this.updateNativeCart(result.sections);
        buzz(fromSheet ? 10 : 12); this.justAdded = items.some(item => String(item.id) === String(this.selected.id));
        clearTimeout(this.addedTimer);
        this.addedTimer = this.later(() => { this.justAdded = false; this.render(); }, 2200);
        if (checkout) {
          if (window.BPLaunchCart && (window.BPLaunch?.state?.().active || window.BPLaunch?.state?.().ended)) await window.BPLaunchCart.sync();
          location.assign(this.route('checkout')); return;
        }
        if (window.BPLaunchCart?.campaignActive()) {
          this.closeSheetForLaunch();
          if (await window.BPLaunchCart.openNative(this.pendingOpener || document.activeElement)) return;
        }
        this.openSheet(true);
        if (fromSheet) this.later(() => {
          const target = !this.$('#shUp2')?.hidden ? this.$('#shMini button:not(:disabled)') : this.$('#shCheckout');
          target?.focus({ preventScroll: true });
        }, 0);
      } catch (error) {
        if (error.name !== 'AbortError') this.toast(posted ? this.t('cartUncertain') : error.message || this.t('cartError'), 3500);
        // Never retry a POST automatically: an uncertain network result may already have added the item.
      } finally { this.busy = false; if (!this.destroyed) this.render(); }
    }
    addSelected(checkout = false) {
      if (!this.selected.available) return;
      const items = [{ id: this.selected.id, quantity: 1 }];
      if (this.pairOn && this.pair?.available) items.push({ id: this.pair.id, quantity: 1 });
      return this.add(items, checkout);
    }
    updateNativeCart(sections) {
      const parser = new DOMParser();
      for (const [id, selector] of [['cart-icon-bubble', '#cart-icon-bubble'], ['cart-drawer', '#CartDrawer']]) {
        if (!sections?.[id]) continue;
        const doc = parser.parseFromString(sections[id], 'text/html');
        const target = document.querySelector(selector);
        const source = doc.querySelector(id === 'cart-icon-bubble' ? '.shopify-section' : selector);
        if (target && source) target.innerHTML = source.innerHTML;
      }
      const bubble = document.querySelector('#cart-icon-bubble .wt-header__panel__counter');
      if (bubble) { bubble.classList.remove('bv-pop'); void bubble.offsetWidth; bubble.classList.add('bv-pop'); }
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bv-pdp', cartData: this.cart });
    }
    shipping() {
      const rules = this.config.shipping || {};
      const known = rules.verified === true && this.cart.currency === 'EUR';
      const free = new Set(rules.freeHandles || []);
      const subtotal = this.cart.items.reduce((sum, item) => sum + (free.has(item.handle) ? 0 : item.final_line_price), 0);
      const threshold = rules.thresholdCents || 4900;
      return { known, subtotal, gap: Math.max(0, threshold - subtotal), isFree: subtotal === 0 || subtotal >= threshold, threshold, rate: rules.rateCents || 699, free };
    }
    renderSheet() {
      if (!this.cart) return;
      const list = this.$('#shList');
      const rows = this.cart.items.map(item => {
        const li = document.createElement('li');
        if (this.fresh.has(String(item.variant_id))) li.className = 'fade';
        const thumb = document.createElement('span'); thumb.className = 'th';
        if (this.config.product.handle === 'combo-eyeliner-mascara' && ['combo-eyeliner-mascara', 'matita-occhi-occhidigatto'].includes(item.handle)) thumb.classList.add('c');
        if (['primer-matte-affina-pori', 'primer-effetto-glow-con-spf30'].includes(this.config.product.handle) && ['primer-matte-affina-pori', 'primer-effetto-glow-con-spf30', '04-correttore-massima-coprenza-copia'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'siero-niacinamide-beautyves' && ['siero-niacinamide-beautyves', 'crema-viso-niacinamide'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'siero-vitamina-c-beautyves' && ['siero-vitamina-c-beautyves', 'crema-viso-vitamina-c-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'siero-collagene-beautyves' && ['siero-collagene-beautyves', 'crema-viso-collagene-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'siero-lifting-occhi' && ['siero-lifting-occhi', 'contorno-occhi-acido-ialuronico-anti-rughe'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'siero-acido-ialuronico-beautyves' && ['siero-acido-ialuronico-beautyves', 'crema-viso-acido-ialuronico-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'crema-viso-niacinamide' && ['crema-viso-niacinamide', 'siero-niacinamide-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'crema-viso-vitamina-c-beautyves' && ['crema-viso-vitamina-c-beautyves', 'siero-vitamina-c-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'acido-salicilico-esfoliante' && ['acido-salicilico-esfoliante', 'crema-viso-niacinamide'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'crema-viso-collagene-beautyves' && ['crema-viso-collagene-beautyves', 'siero-collagene-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'crema-viso-acido-ialuronico-beautyves' && ['crema-viso-acido-ialuronico-beautyves', 'siero-acido-ialuronico-beautyves'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'contorno-occhi-acido-ialuronico-anti-rughe' && ['contorno-occhi-acido-ialuronico-anti-rughe', 'siero-lifting-occhi'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'contorno-occhi-caffeina-anti-borse' && ['contorno-occhi-caffeina-anti-borse', '04-correttore-massima-coprenza-copia'].includes(item.handle)) thumb.classList.add('c');
        if (this.config.product.handle === 'pochette-beautyves' && ['pochette-beautyves', 'volumizzante-labbra'].includes(item.handle)) thumb.classList.add('c');
        const image = document.createElement('img'); image.width = 84; image.height = 104; image.alt = '';
        const local = this.config.variants.find(v => String(v.id) === String(item.variant_id));
        const paired = ['primer-matte-affina-pori', 'primer-effetto-glow-con-spf30'].includes(this.config.product.handle) ? this.config.pair?.variants?.find(v => String(v.id) === String(item.variant_id)) : null;
        this.setImage(image, local?.swatch || paired?.swatch || item.image); thumb.append(image);
        const words = document.createElement('span'); words.className = 'bv-cart-words';
        const title = document.createElement('span'); title.className = 't1'; title.textContent = item.product_title;
        const detail = document.createElement('span'); detail.className = 't2'; detail.textContent = `${item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : ''}${item.quantity > 1 ? ` · ${this.t('pieces', { count: item.quantity })}` : ''}`;
        words.append(title, detail);
        const amount = document.createElement('b'); amount.textContent = this.money(item.final_line_price);
        li.append(thumb, words, amount); return li;
      });
      list?.replaceChildren(...rows); this.fresh.clear();
      const shipping = this.shipping();
      this.$('#shShip')?.classList.toggle('free', shipping.known && shipping.isFree);
      this.text('#shShipTxt', !shipping.known ? this.t('shippingAtCheckout') : shipping.isFree ? this.t('freeShipping') : this.t('shippingGap', { price: this.money(shipping.gap) }));
      const meter = this.$('#shMeter');
      if (meter) this.raf(() => this.raf(() => { meter.style.width = !shipping.known ? '0%' : shipping.isFree ? '100%' : `${clamp(shipping.subtotal / shipping.threshold) * 100}%`; }));
      this.text('#shTot', this.money(this.cart.total_price));
      this.text('#shShipCost', !shipping.known ? this.t('shippingAtCheckout') : shipping.isFree ? this.t('free') : this.money(shipping.rate));
      const hasPair = this.cart.items.some(item => item.handle === this.config.pair?.handle);
      const showPair = !hasPair && this.pair?.available;
      const canCloseGap = shipping.known && !shipping.isFree && !shipping.free.has(this.config.pair?.handle) && this.pair?.priceCents >= shipping.gap;
      const up = this.$('#shUp'); if (up) up.hidden = !showPair;
      this.text('#shUpT', this.t(canCloseGap ? 'pairUpsellFree' : 'pairUpsell'));
      this.text('#shPenAdd', `+ ${this.pair ? this.money(this.pair.priceCents) : ''}`);
      const showExtra = !showPair && ['tinta-labbra-24milabaci', 'matita-labbra-24milabaci'].includes(this.config.product.handle) && (!shipping.known || !shipping.isFree);
      const extra = this.$('#shUp2'); if (extra) extra.hidden = !showExtra;
      const extraCanClose = shipping.known && this.config.variants.filter(v => v.available).every(v => v.priceCents >= shipping.gap);
      this.text('#shUp2T', this.t(extraCanClose ? 'extraUpsellFree' : 'extraUpsell'));
      const checkout = this.$('#shCheckout'); if (checkout) { checkout.href = this.route('checkout'); checkout.removeAttribute('target'); }
    }
    openSheet(added) {
      if (!this.cart?.item_count) { this.toast(this.t('cartEmpty')); return; }
      this.text('#shTitle', this.t(added ? 'cartAdded' : 'cartTitle'));
      const ico = this.$('#shIco'); if (ico) ico.hidden = !added;
      this.renderSheet();
      this.sheet.querySelector('.sheet').scrollTop = 0;
      this.openOverlay(this.sheet, this.sheet.querySelector('[data-close]:not(.scrim)'));
    }
    closeSheetForLaunch() {
      if (this.overlay !== this.sheet) return;
      this.closeOverlay(true, false);
      if (history.state?.bvPdpPanel === this.overlayKey) {
        const state = { ...history.state }; delete state.bvPdpPanel;
        try { history.replaceState(state, '', location.href); } catch (_) { /* history unavailable */ }
      }
    }
    async openCart() {
      if (this.busy || this.cartOpenPending) return;
      const revision = ++this.cartOpenRevision;
      this.cartOpenPending = true;
      const opener = this.pendingOpener?.isConnected ? this.pendingOpener : document.activeElement;
      const href = location.href;
      try {
        if (window.BPLaunchCart?.campaignActive()) {
          this.closeSheetForLaunch();
          if (await window.BPLaunchCart.openNative(opener)) return;
        }
        const cart = await this.request('cart.js');
        if (this.destroyed || revision !== this.cartOpenRevision || !this.root.isConnected || location.href !== href) return;
        this.cart = cart;
        this.pendingOpener = opener;
        this.openSheet(false);
      }
      catch (error) { if (revision === this.cartOpenRevision && error.name !== 'AbortError') this.toast(this.t('cartError'), 3500); }
      finally { if (revision === this.cartOpenRevision) this.cartOpenPending = false; }
    }
    cancelPendingOverlayOpen() {
      this.cartOpenRevision++;
      this.cartOpenPending = false;
      this.pendingOverlayOpen = null;
      if (!this.overlay) this.pendingOpener = null;
    }
    openOverlay(panel, focus) {
      if (!panel) return;
      if (this.historyPending) {
        this.pendingOverlayOpen = { panel, focus,
          opener: this.pendingOpener?.isConnected ? this.pendingOpener : document.activeElement,
          href: location.href };
        this.pendingOpener = null;
        return;
      }
      if (this.overlay === panel) { focus?.focus({ preventScroll: true }); return; }
      if (this.overlay && this.overlay !== panel) this.closeOverlay(true, false);
      if (!this.overlay) this.lastFocus = this.pendingOpener?.isConnected ? this.pendingOpener : document.activeElement;
      this.pendingOpener = null;
      this.overlay = panel; panel.hidden = false;
      this.waClearance?.schedule();
      this.inertBefore = new Map();
      [...document.body.children].forEach(el => {
        if (el === panel || el === this.toastEl) return;
        this.inertBefore.set(el, el.inert); el.inert = true;
      });
      panel.inert = false;
      this.oldOverflow = document.body.style.overflow;
      this.oldPadding = document.body.style.paddingRight;
      const gap = innerWidth - document.documentElement.clientWidth;
      if (gap > 0) document.body.style.paddingRight = `${gap}px`;
      document.body.style.overflow = 'hidden';
      this.modalObserver = new MutationObserver(records => records.forEach(record => record.addedNodes.forEach(el => {
        if (!(el instanceof HTMLElement) || el === panel || el === this.toastEl) return;
        this.inertBefore.set(el, el.inert); el.inert = true;
      })));
      this.modalObserver.observe(document.body, { childList: true });
      try {
        if (history.state?.bvPdpPanel !== this.overlayKey) history.pushState({ ...(history.state || {}), bvPdpPanel: this.overlayKey }, '', location.href);
      } catch (_) { /* browser history unavailable */ }
      focus?.focus({ preventScroll: true });
    }
    closeOverlay(fromHistory = false, restore = true) {
      if (!this.overlay) { this.cancelPendingOverlayOpen(); return; }
      this.pendingOverlayOpen = null;
      this.overlay.hidden = true;
      if (this.overlay === this.lb) this.resetZoom();
      this.overlay = null;
      this.waClearance?.schedule();
      this.modalObserver?.disconnect();
      this.inertBefore?.forEach((was, el) => { if (el.isConnected) el.inert = was; });
      this.inertBefore = null;
      document.body.style.overflow = this.oldOverflow || '';
      document.body.style.paddingRight = this.oldPadding || '';
      if (!fromHistory && history.state?.bvPdpPanel === this.overlayKey) {
        this.historyPending = true;
        try { history.back(); } catch (_) { this.historyPending = false; }
      }
      if (restore && this.lastFocus?.isConnected) this.lastFocus.focus({ preventScroll: true });
    }
    keydown(event) {
      if (!this.overlay) {
        if (event.key === 'Escape' && (this.pendingOverlayOpen || this.historyPending || this.cartOpenPending))
          this.cancelPendingOverlayOpen();
        return;
      }
      if (event.key === 'Escape') { event.preventDefault(); this.closeOverlay(); return; }
      if (this.overlay === this.lb && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault(); this.resetZoom();
        this.$('#lbTrack').scrollBy({ left: (event.key === 'ArrowRight' ? 1 : -1) * this.$('#lbTrack').clientWidth, behavior: this.motion.matches ? 'instant' : 'smooth' }); return;
      }
      if (event.key !== 'Tab') return;
      const controls = [...this.overlay.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')].filter(el => !el.disabled && el.tabIndex >= 0 && visible(el));
      if (!controls.length) return;
      const first = controls[0], last = controls.at(-1);
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
    openPhoto(index) {
      const track = this.$('#lbTrack');
      if (!track) return;
      track.replaceChildren(...this.selected.gallery.map(item => {
        const slide = document.createElement('div'); slide.className = 'lbs';
        const img = document.createElement('img'); img.alt = item.alt || ''; img.decoding = 'async';
        img.src = pictureURL(item.src, 1200); slide.append(img); return slide;
      }));
      this.resetZoom(); this.openOverlay(this.lb, this.$('#lbX'));
      track.scrollTo({ left: track.clientWidth * index, behavior: 'instant' });
      this.text('#lbNum', index + 1);
      this.text('[data-photo-total]', this.selected.gallery.length);
    }
    resetZoom() {
      this.$$('#lbTrack .lbs').forEach(el => el.classList.remove('zoom'));
      this.lb?.classList.remove('zoomed'); this.drag = null;
    }
    zoomPhoto(event) {
      if (this.dragMoved) { this.dragMoved = false; return; }
      const img = event.target.closest('img');
      if (!img) {
        if (event.target.closest('.lbs.zoom')) this.resetZoom();
        else if (!this.lb.classList.contains('zoomed')) this.closeOverlay();
        return;
      }
      const box = img.parentElement;
      if (box.classList.contains('zoom')) { this.resetZoom(); return; }
      const rect = img.getBoundingClientRect();
      const fx = clamp((event.clientX - rect.left) / rect.width), fy = clamp((event.clientY - rect.top) / rect.height);
      box.classList.add('zoom'); this.lb.classList.add('zoomed');
      box.scrollLeft = fx * box.scrollWidth - box.clientWidth / 2;
      box.scrollTop = fy * box.scrollHeight - box.clientHeight / 2;
    }
    quiz() {
      const data = this.config.quiz || {};
      const direct = this.q1 === data.decisive;
      const done = direct || (this.q1 >= 0 && this.q2 >= 0);
      this.$('#q1').hidden = this.q1 >= 0;
      this.$('#q2').hidden = !(this.q1 >= 0 && !direct && this.q2 < 0);
      this.$('#qR').hidden = !done;
      this.$('#qP2').style.background = this.q1 >= 0 ? '#0B0B0C' : 'rgba(11,11,12,.14)';
      this.rec = done ? direct ? data.decisiveVariant : data.mapping?.[`${this.q1}-${this.q2}`] : -1;
      if (data.type === 'concealer' && done) this.rec = Math.max(0, Math.min(this.config.variants.length - 1, this.q2 - (this.q1 === 1 ? 1 : 0)));
      const s = this.config.variants[this.rec];
      if (s) {
        this.setImage(this.$('#qSw'), s.texture || s.swatch || s.hero);
        this.text('#qName', s.label || s.title); this.text('#qMood', s.mood || ''); this.text('#qBtn', s.name || s.title);
      }
      this.render();
    }
    measureHeader() {
      const header = document.querySelector('sticky-header, .shopify-section-header, #shopify-section-header, header');
      const rect = header?.getBoundingClientRect();
      const height = rect && rect.bottom > 0 && rect.top < innerHeight ? Math.min(rect.bottom, 220) : 0;
      [this.root, ...this.panels].forEach(el => el.style.setProperty('--bv-header-height', `${Math.max(0, height)}px`));
    }
    initWhatsAppClearance() {
      const marker = 'data-bv-pdp-wa-lift', property = '--bv-wa-bottom';
      const state = { widget: null, lift: 0, naturalBottom: 0, originalValue: '', originalPriority: '',
        frame: false, resizeObserver: null, intersectionObserver: null, discovery: null };
      const visible = rect => rect.width > 0 && rect.height > 0 && rect.right > 0 && rect.left < innerWidth &&
        rect.bottom > 0 && rect.top < innerHeight;
      const intersects = (a, b) => Math.min(a.right, b.right) > Math.max(a.left, b.left) &&
        Math.min(a.bottom, b.bottom) > Math.max(a.top, b.top);
      const clearLift = (widget = state.widget) => {
        if (widget?.getAttribute(marker) === this.overlayKey) {
          widget.removeAttribute(marker);
          if (state.originalValue) widget.style.setProperty(property, state.originalValue, state.originalPriority);
          else widget.style.removeProperty(property);
        }
        state.lift = 0;
      };
      const attach = () => {
        const widget = document.getElementById('wa-chat-btn-root');
        if (widget === state.widget) return;
        if (state.widget) { clearLift(); state.resizeObserver?.unobserve(state.widget); }
        state.widget = widget;
        if (!widget) return;
        state.originalValue = widget.style.getPropertyValue(property);
        state.originalPriority = widget.style.getPropertyPriority(property);
        state.naturalBottom = parseFloat(getComputedStyle(widget).bottom);
        state.lift = 0;
        state.resizeObserver?.observe(widget);
      };
      const measure = () => {
        if (this.destroyed || !this.root.isConnected) return;
        attach();
        const widget = state.widget;
        if (!widget || this.overlay || widget.inert || getComputedStyle(widget).position !== 'fixed' ||
            !Number.isFinite(state.naturalBottom)) { clearLift(); return; }
        const moved = widget.getBoundingClientRect();
        if (!visible(moved)) { clearLift(); return; }
        // Reconstruct the app's unmodified position, rather than measuring our own lifted position.
        const natural = { left: moved.left, right: moved.right, top: moved.top + state.lift,
          bottom: moved.bottom + state.lift };
        const collisions = this.$$('.bar .cta').map(el => el.getBoundingClientRect())
          .filter(rect => visible(rect) && intersects(rect, natural));
        if (!collisions.length) { clearLift(); return; }
        let lift = Math.ceil(Math.max(...collisions.map(rect => natural.bottom - rect.top + 12)));
        const header = document.querySelector('sticky-header,.shopify-section-header,#shopify-section-header,header');
        const headerBottom = Math.max(0, Math.min(220, header?.getBoundingClientRect().bottom || 0));
        lift = Math.min(lift, Math.max(0, natural.top - headerBottom - 12));
        if (lift <= 0 || lift === state.lift) return;
        widget.style.setProperty(property, `${state.naturalBottom + lift}px`);
        widget.setAttribute(marker, this.overlayKey);
        state.lift = lift;
      };
      const schedule = () => {
        if (state.frame || this.destroyed) return;
        state.frame = true;
        this.raf(() => { state.frame = false; measure(); });
      };
      const resetOnResize = () => {
        clearLift();
        if (state.widget) state.naturalBottom = parseFloat(getComputedStyle(state.widget).bottom);
        schedule();
      };
      this.on(window, 'scroll', schedule, { passive: true });
      this.on(window, 'resize', resetOnResize, { passive: true });
      if (typeof ResizeObserver === 'function') {
        state.resizeObserver = new ResizeObserver(schedule);
        this.$$('.bar .cta').forEach(el => state.resizeObserver.observe(el));
      }
      if (typeof IntersectionObserver === 'function') {
        state.intersectionObserver = new IntersectionObserver(schedule, { threshold: [0, 1] });
        this.$$('.bar .cta').forEach(el => state.intersectionObserver.observe(el));
      }
      state.discovery = new MutationObserver(schedule);
      state.discovery.observe(document.body, { childList: true });
      document.fonts?.ready?.then(schedule).catch(() => {});
      attach(); schedule();
      this.waClearance = { schedule, cleanup: () => {
        state.discovery.disconnect(); state.resizeObserver?.disconnect(); state.intersectionObserver?.disconnect();
        clearLift(); state.widget = null;
      } };
    }
    initMotion() {
      const story = this.$('#story'), stage = this.$('#stage');
      if (!story || !stage) return;
      const E = { glow: this.$('#stGlow'), bottle: this.$('#stBottle'), sw: this.$('#stSwatch'), flood: this.$('#stFlood'), gloss: this.$('#stGloss'), count: this.$('#stCount'), t: [1, 2, 3, 4, 5].map(i => this.$(`#t${i}`)), row: this.$$('#stRow .st-sw'), giant: this.$('#t5 .st-giant') };
      const windows = [[0, .24], [.22, .44], [.42, .64], [.62, .84], [.82, 1.3]];
      let ticking = false;
      const frame = () => {
        ticking = false;
        this.measureHeader();
        if (this.motion.matches) {
          for (const el of [E.glow, E.bottle, E.sw, E.flood, E.gloss, E.giant, ...E.row, ...E.t].filter(Boolean)) { el.style.removeProperty('opacity'); el.style.removeProperty('transform'); el.style.removeProperty('pointer-events'); }
          stage.classList.remove('st-light'); E.row.forEach(el => { el.inert = false; }); E.t[4].inert = false;
          return;
        }
        const r = story.getBoundingClientRect(), vh = innerHeight;
        if (r.bottom < 0 || r.top > vh) return;
        const p = clamp(-r.top / Math.max(1, r.height - vh));
        const s1 = seg(p, 0, .2), out1 = seg(p, .16, .26);
        const shake = Math.sin(s1 * Math.PI * 10) * 11 * (1 - s1 * .85);
        if (this.config.product.handle === 'combo-eyeliner-mascara') {
          const returnIn = ease(seg(p, .41, .52));
          const returnOut = seg(p, .82, .92);
          E.bottle.style.opacity = Math.max(1 - out1, returnIn * (1 - returnOut));
          E.bottle.style.transform = returnIn > 0
            ? `translate(calc(-50% + 64px),-50%) translateY(${16 - returnIn * 16}px) rotate(5deg) scale(${.78 + returnIn * .16})`
            : `translate(-50%,-50%) translateY(${Math.sin(s1 * Math.PI * 5) * -10}px) rotate(${shake - out1 * 14}deg) scale(${.94 + s1 * .1 + out1 * .25})`;
        } else if (['primer-matte-affina-pori', 'primer-effetto-glow-con-spf30', 'siero-niacinamide-beautyves', 'siero-vitamina-c-beautyves', 'siero-collagene-beautyves', 'siero-lifting-occhi', 'siero-acido-ialuronico-beautyves', 'crema-viso-niacinamide', 'crema-viso-vitamina-c-beautyves', 'acido-salicilico-esfoliante', 'crema-viso-collagene-beautyves', 'crema-viso-acido-ialuronico-beautyves', 'contorno-occhi-acido-ialuronico-anti-rughe', 'contorno-occhi-caffeina-anti-borse', 'pochette-beautyves'].includes(this.config.product.handle)) {
          const glide = ease(seg(p, .18, .4));
          const settle = ease(seg(p, .41, .52));
          E.bottle.style.opacity = 1 - seg(p, .82, .92);
          E.bottle.style.transform = `translate(-50%,-50%) translateY(${-12 * glide + 12 * settle}px) rotate(${-5 + glide * 10 - settle * 5}deg) scale(${1 + glide * .08 - settle * .08})`;
        } else if (this.config.product.handle === 'volumizzante-labbra' || this.config.product.handle === 'mascara-volumizzante-incurvante' || this.config.product.handle === 'eyeliner-ultra-precision' || this.config.product.handle === 'matita-occhi-occhidigatto') {
          const returnIn = ease(seg(p, .41, .52));
          const returnOut = seg(p, .82, .92);
          E.bottle.style.opacity = Math.max(1 - out1, returnIn * (1 - returnOut));
          E.bottle.style.transform = returnIn > 0
            ? `translate(-50%,-50%) translateY(${16 - returnIn * 16}px) rotate(${-8 + returnIn * 5}deg) scale(${.78 + returnIn * .16})`
            : `translate(-50%,-50%) translateY(${Math.sin(s1 * Math.PI * 5) * -10}px) rotate(${shake - out1 * 25}deg) scale(${.94 + s1 * .1 + out1 * .35})`;
        } else {
          E.bottle.style.opacity = 1 - out1;
          E.bottle.style.transform = `translate(-50%,-50%) translateY(${Math.sin(s1 * Math.PI * 5) * -10}px) rotate(${shake - out1 * 25}deg) scale(${.94 + s1 * .1 + out1 * .35})`;
        }
        E.glow.style.opacity = 1 - seg(p, .2, .32);
        const g = ease(seg(p, .19, .4));
        const resolution = Math.min(1, (E.sw.naturalHeight || 889) / 889);
        E.sw.style.opacity = seg(p, .18, .24) * (1 - seg(p, .33, .39));
        E.sw.style.transform = `translate(-50%,-50%) rotate(${-12 + g * 24}deg) scale(${.55 + g * 1.35 * resolution})`;
        if (this.config.product.handle === 'combo-eyeliner-mascara') {
          const early = seg(p, .18, .26) * (1 - seg(p, .35, .42));
          const late = ease(seg(p, .41, .52)) * (1 - seg(p, .82, .92));
          E.sw.style.opacity = Math.max(early, late);
          E.sw.style.transform = `translate(calc(-50% - 64px),-50%) rotate(-5deg) scale(${.78 + Math.max(early, late) * .16})`;
        }
        const flood = seg(p, .29, .37) * (1 - seg(p, .62, .7));
        E.flood.style.opacity = flood;
        E.flood.style.transform = `scale(${1.25 - .25 * seg(p, .29, .62)}) rotate(${seg(p, .29, .7) * 12}deg)`;
        stage.classList.toggle('st-light', flood > .5 && this.selected.lightText !== false);
        E.gloss.style.opacity = seg(p, .42, .47) * (1 - seg(p, .5, .62)) * .9;
        E.gloss.style.transform = `translateX(${(seg(p, .42, .62) - .5) * 34}%)`;
        E.row.forEach((el, k) => {
          const inn = ease(seg(p, .64 + k * .025, .72 + k * .025)), out = seg(p, .8, .86);
          el.style.opacity = inn * (1 - out);
          el.style.transform = `translateY(${(1 - inn) * 90 - out * 30}px) rotate(${(1 - inn) * (k - 2) * 7}deg)`;
          const interactive = inn > .35 && out < .6;
          el.style.pointerEvents = interactive ? 'auto' : 'none'; el.inert = !interactive;
        });
        let current = 0, best = -1;
        E.t.forEach((el, k) => {
          const x = seg(p, ...windows[k]), opacity = clamp(Math.min(x / .2, (1 - x) / .2));
          el.style.opacity = opacity;
          el.style.transform = `translateY(${(1 - seg(p, windows[k][0], windows[k][0] + .05)) * 24}px)`;
          if (opacity > best) { best = opacity; current = k; }
        });
        E.giant.style.transform = `scale(${2.6 - 1.6 * ease(seg(p, .82, .95))})`;
        E.t[4].style.pointerEvents = p > .88 ? 'auto' : 'none'; E.t[4].inert = p <= .88;
        E.count.textContent = `0${current + 1} / 05`;
      };
      const schedule = () => {
        if (ticking) return;
        // An offscreen story has no RAF loop or decorative work.
        const r = story.getBoundingClientRect();
        if (!this.motion.matches && (r.bottom < 0 || r.top > innerHeight)) return;
        ticking = true; this.raf(frame);
      };
      this.on(window, 'scroll', schedule, { passive: true }); this.on(window, 'resize', schedule);
      this.on(this.motion, 'change', frame); frame();
    }
    bind() {
      this.on(this.root, 'click', event => {
        if (!this.overlay && (this.historyPending || this.pendingOverlayOpen || this.cartOpenPending))
          this.cancelPendingOverlayOpen();
        const option = event.target.closest('#swatches .sw,#wornPills .pill,#rail .rd,#stRow .st-sw');
        if (option?.dataset.i != null) { this.setShade(Number(option.dataset.i)); return; }
        if (event.target.closest('.js-add')) { event.preventDefault(); this.pendingOpener = event.target.closest('.js-add'); this.addSelected(); return; }
        if (event.target.closest('#buyNow')) { event.preventDefault(); this.addSelected(true); return; }
        if (event.target.closest('#penToggle')) { this.togglePair(); return; }
        if (event.target.closest('.js-penadd')) { if (this.config.product.single) { if (this.pair?.available && !this.busy) { this.pendingOpener = event.target.closest('.js-penadd'); this.add([{ id: this.pair.id, quantity: 1 }]); } } else this.togglePair(); return; }
        const pair = event.target.closest('#penPills .pill,#pairPills .pill');
        if (pair) { if (!this.busy && !pair.disabled) { this.justAdded = false; clearTimeout(this.addedTimer); this.pairIndex = Number(pair.dataset.p); this.render(); } return; }
        if (event.target.closest('#fav')) { this.favorite(); return; }
        if (event.target.closest('#share')) { this.share(); return; }
        if (event.target.closest('#gZoom')) { this.pendingOpener = this.$('#gZoom'); this.openPhoto(this.galleryIndex); return; }
        const slide = event.target.closest('#gal .slide');
        if (slide && !event.target.closest('button,a')) { this.pendingOpener = this.$('#gZoom'); this.openPhoto([...this.gallery.children].indexOf(slide)); return; }
        const a = event.target.closest('[data-q1]'), b = event.target.closest('[data-q2]');
        if (a) { this.q1 = Number(a.dataset.q1); this.q2 = -1; this.quiz(); return; }
        if (b) { this.q2 = Number(b.dataset.q2); this.quiz(); return; }
        if (event.target.closest('#qBack,#qReset')) { this.q1 = -1; this.q2 = -1; this.quiz(); return; }
        if (event.target.closest('#qApply,#qAdd') && this.rec >= 0) { this.setShade(this.rec); if (event.target.closest('#qAdd')) { this.pendingOpener = this.$('#qAdd'); this.addSelected(); } }
      });
      this.on(this.root, 'submit', event => { if (event.target.matches('[data-bv-form]')) { event.preventDefault(); this.pendingOpener = event.submitter || document.activeElement; this.addSelected(); } });
      this.on(this.$('#swatches'), 'keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault(); const n = this.config.variants.length;
        const index = event.key === 'Home' ? 0 : event.key === 'End' ? n - 1 : (this.current + (event.key === 'ArrowRight' ? 1 : n - 1)) % n;
        this.setShade(index); this.$$('#swatches .sw')[index]?.focus();
      });
      this.on(this.gallery, 'scroll', () => {
        if (this.galleryRAF) return;
        this.galleryRAF = this.raf(() => { this.galleryRAF = 0; const width = this.gallery.firstElementChild?.getBoundingClientRect().width || 1; this.updateDots(Math.round(this.gallery.scrollLeft / (width + 8))); });
      }, { passive: true });
      this.panels.forEach(panel => this.on(panel, 'click', async event => {
        if (event.target.closest('#shCheckout') && this.busy) { event.preventDefault(); return; }
        if (event.target.closest('#shCheckout') && window.BPLaunchCart &&
            (window.BPLaunch?.state?.().active || window.BPLaunch?.state?.().ended)) {
          event.preventDefault();
          if (this.launchCheckoutPending) return;
          this.launchCheckoutPending = true;
          const link = this.$('#shCheckout'); link?.setAttribute('aria-disabled', 'true');
          try {
            const cart = await window.BPLaunchCart.sync();
            if (window.BPLaunch?.state?.().ended && cart?.items?.some(window.BPLaunchCart.isGift)) throw new Error(this.t('cartError'));
            location.assign(this.route('checkout'));
          } catch (error) { this.toast(error.message || this.t('cartError'), 3500); }
          finally { this.launchCheckoutPending = false; link?.removeAttribute('aria-disabled'); }
          return;
        }
        if (event.target.closest('[data-close],#lbX')) { this.closeOverlay(); return; }
        const pair = event.target.closest('#sheetPairPills .pill');
        if (pair) { if (!this.busy && !pair.disabled) { this.pairIndex = Number(pair.dataset.p); this.render(); this.renderSheet(); } return; }
        if (event.target.closest('#shPenAdd') && this.pair?.available) this.add([{ id: this.pair.id, quantity: 1 }]);
        const extra = event.target.closest('#shMini button');
        if (extra) { const v = this.config.variants[Number(extra.dataset.i)]; if (v?.available) this.add([{ id: v.id, quantity: 1 }]); }
      }));
      const track = this.$('#lbTrack');
      this.on(track, 'click', event => this.zoomPhoto(event));
      this.on(track, 'scroll', () => this.text('#lbNum', Math.round(track.scrollLeft / Math.max(1, track.clientWidth)) + 1), { passive: true });
      this.on(track, 'dragstart', event => event.preventDefault());
      this.on(track, 'pointerdown', event => {
        const box = event.target.closest('.lbs.zoom');
        if (!box || event.pointerType === 'touch') return;
        this.dragMoved = false;
        this.drag = { box, x: event.clientX, y: event.clientY, left: box.scrollLeft, top: box.scrollTop };
        box.setPointerCapture(event.pointerId);
      });
      this.on(track, 'pointermove', event => {
        if (!this.drag) return;
        const dx = event.clientX - this.drag.x, dy = event.clientY - this.drag.y;
        if (Math.abs(dx) + Math.abs(dy) > 4) this.dragMoved = true;
        this.drag.box.scrollLeft = this.drag.left - dx; this.drag.box.scrollTop = this.drag.top - dy;
      });
      this.on(track, 'pointerup', () => { this.drag = null; }); this.on(track, 'pointercancel', () => { this.drag = null; });
      this.on(document, 'keydown', event => this.keydown(event));
      this.on(window, 'popstate', () => {
        const wasHistoryPending = this.historyPending;
        this.historyPending = false;
        if (this.overlay && history.state?.bvPdpPanel !== this.overlayKey) this.closeOverlay(true);
        if (!wasHistoryPending && !this.overlay && this.cartOpenPending) this.cancelPendingOverlayOpen();
        const queued = this.pendingOverlayOpen;
        if (queued && history.state?.bvPdpPanel !== this.overlayKey) {
          this.pendingOverlayOpen = null;
          if (!this.destroyed && !this.overlay && this.root.isConnected &&
              queued.panel.isConnected && location.href === queued.href) {
            this.pendingOpener = queued.opener?.isConnected ? queued.opener : null;
            this.openOverlay(queued.panel, queued.focus);
          }
        }
      });
      // Capture only real header cart triggers, only while this scoped PDP exists.
      this.on(document.documentElement, 'click', event => {
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const trigger = event.target.closest('.wt-cart__trigger,#cart-icon-bubble,a[href="/cart"]');
        if (!trigger || this.root.contains(trigger) || trigger.closest('cart-drawer,[data-bv-owner]') || !this.root.isConnected) return;
        if (this.historyPending && !this.overlay) this.cancelPendingOverlayOpen();
        event.preventDefault(); event.stopImmediatePropagation(); this.pendingOpener = trigger; this.openCart();
      }, { capture: true });
      this.on(window, 'resize', () => this.measureHeader());
      this.on(document, 'bp:launch:tick', () => {
        if (this.overlay === this.sheet && window.BPLaunchCart?.campaignActive() && !this.cartOpenPending) this.openCart();
      });
      this.on(document, 'bp:launch:cart', event => {
        if (!this.destroyed && event.detail?.cart) {
          this.cart = event.detail.cart;
          if (this.overlay === this.sheet) this.renderSheet();
        }
      });
    }
    destroy() {
      this.cancelPendingOverlayOpen();
      this.paymentPlacement?.cleanup();
      this.paymentPlacement = null;
      this.waClearance?.cleanup();
      this.waClearance = null;
      if (this.overlay) this.closeOverlay(true, false);
      if (history.state?.bvPdpPanel === this.overlayKey) {
        const state = { ...history.state }; delete state.bvPdpPanel;
        try { history.replaceState(state, '', location.href); } catch (_) { /* unavailable history */ }
      }
      this.destroyed = true; this.abort.abort();
      this.timers.forEach(clearTimeout); this.frames.forEach(cancelAnimationFrame);
      this.panels.forEach(panel => { if (this.root.isConnected) this.root.append(panel); else panel.remove(); });
      delete this.root.dataset.bvReady;
    }
  }
  const init = scope => {
    const roots = scope.matches?.('[data-bv-pdp]') ? [scope] : [...scope.querySelectorAll('[data-bv-pdp]')];
    roots.forEach(root => {
      if (instances.has(root)) return;
      try { instances.set(root, new BvPdp(root)); } catch (error) { console.error('BeautyVes PDP initialization failed', error); }
    });
  };
  const dispose = scope => { for (const [root, instance] of instances) if (scope === root || scope.contains(root) || !root.isConnected) { instance.destroy(); instances.delete(root); } };
  window.BvPdpLifecycle = { init, dispose };
  document.addEventListener('shopify:section:load', event => { dispose(event.target); init(event.target); });
  document.addEventListener('shopify:section:unload', event => dispose(event.target));
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true }); else init(document);
})();
